"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { Button } from "@/components/ui/button"
import { useI18n } from "@/i18n"
import { BILLING_MODE } from "@/lib/billingMode"
import type { AuthMode } from "@/components/auth/authMode"
import { AppStoreLink } from "./AppStoreLink"
import { Reveal } from "./Reveal"

interface FinalCtaProps {
  onOpenAuth: (mode: AuthMode) => void
}

export function FinalCta({ onOpenAuth }: FinalCtaProps) {
  const { t } = useI18n()

  const primary = (
    <Button type="button" size="lg" className="min-h-11 shrink-0" onClick={() => onOpenAuth("signUp")}>
      {t(BILLING_MODE === "app_store" ? "landingPage.finalCta.ctaAppStore" : "landingPage.finalCta.cta")}
    </Button>
  )
  // app_store mode only: the App Store link sits next to the primary CTA (stacked under it on phones). Any other
  // mode renders the bare button, exactly as before.
  const cta =
    BILLING_MODE === "app_store" ? (
      <div className="flex shrink-0 flex-col items-start gap-3 sm:flex-row sm:items-center">
        {primary}
        <AppStoreLink placement="final_cta" className="min-h-11" />
      </div>
    ) : (
      primary
    )

  return (
    <section className="border-b border-line bg-paper">
      <Reveal
        as="div"
        className="mx-auto flex max-w-6xl flex-col items-start gap-4 px-4 py-16 md:flex-row md:items-center md:justify-between md:px-8"
      >
        <div>
          <p className="text-sm font-semibold tracking-wide text-green uppercase">
            {t("landingPage.finalCta.eyebrow")}
          </p>
          <h2 className="mt-2 text-3xl font-bold text-ink md:text-4xl">
            {t("landingPage.finalCta.heading")}
          </h2>
          <p className="mt-3 max-w-prose text-ink-2">{t("landingPage.finalCta.body")}</p>
        </div>
        {cta}
      </Reveal>
    </section>
  )
}
